import {DependencyInjectId} from "@@/package/const/DependencyInjectId";
// @ts-ignore
import UserApi from "@@/package/apis/user/userApi";
import {UserHelper} from "@@/package/mapper/user/UserHelper";
import {type ApplicationContextInterface} from "@@/package/application/applicationContext";
import type UserAccountRequest from "~~/package/apis/user/request/UserAccountRequest";
import type UserV2Response from "~~/package/apis/user/response/UserV2Response";
import {CountryModel} from "~~/package/mapper/user/model/CountryModel";
import {UserV2Model} from "~~/package/mapper/user/model/UserV2Model";
import type CustomResponse from "~~/package/types/ResponseType";
import { UserDashboardSummaryModel } from "~~/package/mapper/user/model/UserDashboardSummaryModel";
import {UserPasswordChangeModel} from '~~/package/mapper/user/model/UserPasswordChangeModel';
import type {ResendEmailType} from '~~/package/mapper/user/model/ResendEmailType';

export default class UserMapper {
    protected userApi: UserApi;

    constructor(context: ApplicationContextInterface) {
        this.userApi = context.getService<UserApi>(DependencyInjectId.UserApi);
    }

    public async setXCountry(): Promise<void> {
        await this.userApi.setXCountry();
    }

    public async getUserV2(loginTime: number): Promise<UserV2Model> {
        const userResponse: CustomResponse<UserV2Response> = (await this.userApi.getUserV2(loginTime)).data;
        return UserHelper.mappingUserV2(userResponse.data);
    }

    public async userSignIn(email: string, password: string): Promise<string> {
        const userAccessToken: CustomResponse<string> = (await this.userApi.signIn(email, password)).data;
        const accessToken = userAccessToken.data;
        return accessToken;
    }

    public async userSignUp(email: string, password: string, userType: string): Promise<boolean> {
        const userResponseData: CustomResponse<boolean> = (await this.userApi.signUp(email, password, userType)).data;
        return userResponseData.data;
    }

    public async userAuthToken(userId: number, token: string): Promise<string> {
        const userAccessToken: CustomResponse<string> = (await this.userApi.userAuthEmail(userId, token)).data;
        return userAccessToken.data
    }

    // SEC-1 (TSK-10568): tells the backend to clear the HttpOnly AccessToken cookie.
    public async userSignOut(): Promise<void> {
        try {
            await this.userApi.signOut();
        } catch {
            // best-effort; cookie cleanup at callsites still proceeds
        }
    }

    public async userResendEmail(email: string, emailType: ResendEmailType, assessmentId?: number): Promise<boolean> {
        const userResponseData: CustomResponse<boolean> = (await this.userApi.userResendEmail(email, emailType, assessmentId)).data;
        return userResponseData.data
    }

    public async userSendAuthEmail(email: string): Promise<boolean> {
        const userResponseData: CustomResponse<boolean> = (await this.userApi.userSendAuthEmail(email)).data;
        return userResponseData.data
    }

    public async userPasswordChangeAuthEmail(userId: number, token: string): Promise<boolean> {
        const userAccessToken: CustomResponse<boolean> = (await this.userApi.userPasswordChangeAuthEmail(userId, token)).data;
        return userAccessToken.data
    }

    public async userSendPasswordChangeAuthEmail(email: string): Promise<boolean> {
        const userResponseData: CustomResponse<boolean> = (await this.userApi.userSendPasswordChangeAuthEmail(email)).data;
        return userResponseData.data
    }

    public async userPasswordChange(userId: number, token: string, newPassword: string): Promise<UserPasswordChangeModel> {
        const userResponseData: CustomResponse<UserPasswordChangeModel> = (await this.userApi.userPasswordChange(userId, token, newPassword)).data;
        return userResponseData.data
    }

    public async userAccountSetting(userAccountRequest: UserAccountRequest): Promise<boolean> {
        const userResponseData: CustomResponse<boolean> = (await this.userApi.userAccountSetting(userAccountRequest)).data;
        return userResponseData.data;
    }

    public async updateUserTrackSkipFlag(trackSkipFlag: {[key: string]: boolean}): Promise<boolean> {
        const userResponseData: CustomResponse<boolean> = (await this.userApi.updateUserTrackSkipFlag(trackSkipFlag)).data;
        return userResponseData.data;
    }

    public async getCountryList(isToContactUs: boolean): Promise<CountryModel[]> {
        const countryListResponseData: CustomResponse<CountryModel[]> = (await this.userApi.getCountryList(isToContactUs)).data;
        if(isToContactUs) {
            return countryListResponseData.data.map(countryData =>
                UserHelper.mappingCountryDataForContactUs(countryData)
            );
        } else {
            return countryListResponseData.data.map(countryData =>
                UserHelper.mappingCountryData(countryData)
            );
        }
    }

    public async setUserCountryData(userId: string, countryRegionId: string): Promise<boolean> {
        const userResponseData: CustomResponse<boolean> = (await this.userApi.setUserCountryData(userId, countryRegionId)).data;
        return userResponseData.data
    }

    public async setMarketingTerms(allowMarketing: boolean): Promise<boolean> {
        const userResponseData: CustomResponse<boolean> = (await this.userApi.setMarketingTerms(allowMarketing)).data;
        return userResponseData.data
    }

    public async setUserLocale(locale: string): Promise<boolean> {
        const userResponseData: CustomResponse<boolean> = (await this.userApi.setUserLocale(locale)).data;
        return userResponseData.data
    }

    public async changePassword(newPassword: string, oldPassword?: string): Promise<boolean> {
        const userResponseData: CustomResponse<boolean> = (await this.userApi.changePassword(newPassword, oldPassword)).data;
        return userResponseData.data
    }

    public async deleteAccount(): Promise<boolean> {
        const userResponseData: CustomResponse<boolean> = (await this.userApi.deleteAccount()).data;
        return userResponseData.data
    }

    public async getUserDashboardSummary(): Promise<UserDashboardSummaryModel> {
        const userResponseData: CustomResponse<UserDashboardSummaryModel> = (await this.userApi.getUserDashboardSummary()).data;
        return userResponseData.data
    }

    public async getBlockedEmailDomains(): Promise<string[]> {
        const userResponseData: CustomResponse<string[]> = (await this.userApi.getBlockedEmailDomains()).data;
        return userResponseData.data
    }
}
